import React, { useEffect, useMemo, useRef } from 'react';
import { Layer } from 'react-konva';
import { getConfigs, getElements, getRootElements } from '@starflow/app/extension';
import { useViewport, useTempState, useProjectState } from '@starflow/app/store';
import helpers from '@starflow/app/helpers';
import { ImageElement } from './image';
import { GroupElement } from './group';
import { ElementWrapper } from './Wrapper';
import './styles.scss';

export { Overlayers } from './Overlayers';

export const coreElements = {
  image: ImageElement,
  group: GroupElement,
};

export const Elements = () => {
  const viewport = useViewport();
  const elements = useProjectState('elements');
  const editingElementId = useProjectState('editingElementId');
  const currentElementTool = useProjectState('currentElementTool');
  const selection = useProjectState('selection');
  const layerRef = useRef(null);
  const selectionMap = useMemo(() => {
    return Object.fromEntries(selection.map((id) => [id, true]));
  }, [selection]);

  const spacePressing = useTempState('spacePressing');
  const currentCanvasTool = useTempState('currentCanvasTool');
  const hiddenElementIds = useTempState('hiddenElementIds');
  const extensionElements = getElements();

  const [lowerElements, upperElements] = useMemo(() => {
    const rootElements = getRootElements();

    return [
      rootElements.lower.map(({ Component, config }, index) => <Component key={index} {...config} />),
      rootElements.upper.map(({ Component, config }, index) => <Component key={index} {...config} />),
    ];
  }, []);

  const isDraggingMode = spacePressing || currentCanvasTool === 'move';
  const draggable = currentCanvasTool === 'select';

  const handleElementDragEnd = (element, attrs) => {
    helpers.canvas.updateElement(element.id, {
      x: attrs.x,
      y: attrs.y,
    });
  };

  useEffect(() => {
    const canvasDom = layerRef.current.getCanvas();
    canvasDom._canvas.id = 'element-canvas';
  }, []);

  return (
    <Layer id="elementsLayer" ref={layerRef}>
      {lowerElements}
      {elements.map((element) => {
        if (element.hidden) return null;
        if (hiddenElementIds && hiddenElementIds[element.id]) return null;

        const Element = extensionElements[element.type] || coreElements[element.type];
        if (!Element) return null;

        const elementConfig = getConfigs()[element.type];

        return (
          <ElementWrapper
            key={element.id}
            element={element}
            draggable={draggable && !isDraggingMode}
            selectionMap={selectionMap}
            currentElementTool={currentElementTool}
            editingElementId={editingElementId}
            onDragEnd={handleElementDragEnd}
            config={elementConfig}
          >
            <Element
              element={element}
              viewport={viewport}
              config={elementConfig}
              draggable={!isDraggingMode}
              currentElementTool={currentElementTool}
              currentCanvasTool={currentCanvasTool}
              editingElementId={editingElementId}
            />
          </ElementWrapper>
        );
      })}
      {upperElements}
    </Layer>
  );
};
